import _ from 'lodash';
import { CalculatorType } from 'spw-redux/modules/calculators/types';
import { cond, equals, always, T } from 'ramda';
import {
  GROWTH_CALCULATORS as Calculators,
  MEETING_SPACES_CATEGORY,
  AMENITY_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
} from 'spw-constants';
import { Category, Subcategory } from 'spw-redux/modules/space_types/types';
import * as Sentry from '@sentry/react';

export type GACategory = 'Existing Site Analysis' | 'Programming';

export interface GAEvent {
  action: string;
  category: GACategory;
  subCategory: string;
  label: string;
  value?: number;
}

export const event = ({
  action,
  category,
  subCategory,
  label,
  value,
}: GAEvent) => {
  try {
    window.gtag('event', action.toUpperCase(), {
      event_category: `${category}/${subCategory}`,
      event_label: label,
      value,
    });
  } catch (error) {
    Sentry.captureException(error);
    /* Fail silent, Do nothing */
  }
};

export const tag = (name: string, projectType: string) =>
  `${_.lowerCase(projectType) === 'programming' ? 'PROG' : 'ESA'}_${name}`;

export const tagFromSaveCalculator: (
  calculator: CalculatorType
) => string = cond([
  [
    equals(Calculators.buildingHeadcountSeatcount),
    always('PROG_BHCSC_CALC_SAVE'),
  ],
  [
    equals(Calculators.buildingHeadcountSeatcountDepartments),
    always('PROG_BHCSCD_CALC_SAVE'),
  ],
  [equals(Calculators.floorHeadcountSeatcount), always('PROG_FHCSC_CALC_SAVE')],
  [equals(Calculators.buildingUsf), always('PROG_BUSF_CALC_SAVE')],
  [equals(Calculators.floorUsf), always('PROG_FUSF_CALC_SAVE')],
  [equals(Calculators.macroProgramming), always('MACRO_PROG_SCEN_SAVE')],
  [equals(Calculators.totalProgram), always('PROG_TOTAL_PROG_SCEN_SAVE')],
  [T, always('invalid calculator type save')],
]);

export const tagFromDupCalculator: (
  calculator: CalculatorType
) => string = cond([
  [
    equals(Calculators.buildingHeadcountSeatcount),
    always('PROG_BLDG_SCEN_DUPE'),
  ],
  [
    equals(Calculators.buildingHeadcountSeatcountDepartments),
    always('PROG_BLDG_SCEN_DUPE'),
  ],
  [equals(Calculators.floorHeadcountSeatcount), always('PROG_FL_SCEN_DUPE')],
  [equals(Calculators.buildingUsf), always('PROG_BLDG_SCEN_DUPE')],
  [equals(Calculators.floorUsf), always('PROG_FL_SCEN_DUPE')],
  [equals(Calculators.macroProgramming), always('MACRO_PROG_SCEN_DUPE')],
  [T, always('invalid calculator type dupe')],
]);

export const tagFromExportCalculator: (
  calculator: CalculatorType
) => string = cond([
  [
    equals(Calculators.buildingHeadcountSeatcount),
    always('PROG_BLDG_SCEN_EXPORT'),
  ],
  [
    equals(Calculators.buildingHeadcountSeatcountDepartments),
    always('PROG_BLDG_SCEN_EXPORT'),
  ],
  [equals(Calculators.floorHeadcountSeatcount), always('PROG_FL_SCEN_EXPORT')],
  [equals(Calculators.buildingUsf), always('PROG_BLDG_SCEN_EXPORT')],
  [equals(Calculators.floorUsf), always('PROG_FL_SCEN_EXPORT')],
  [equals(Calculators.macroProgramming), always('MACRO_PROG_SCEN_EXPORT')],
  [equals(Calculators.totalProgram), always('TOTAL_PROG_SCEN_EXPORT')],
  [T, always('invalid calculator type export')],
]);

export const tagFromSaveSpace: (
  category: Category | Subcategory
) => string = cond([
  [equals(INDIVIDUAL_WORKSPACES_CATEGORY), always('ESA_INDV_SPACE_ADD')],
  [equals(MEETING_SPACES_CATEGORY), always('ESA_MTG_SPACE_ADD')],
  [equals(AMENITY_SPACES_CATEGORY), always('ESA_AMNTY_SPACE_ADD')],
  [equals(SUPPORT_SPACES_CATEGORY), always('ESA_SPRT_SPACE_ADD')],
  [T, always('invalid category')],
]);

export const saveCategoryFromCalculator: (
  calculator: CalculatorType
) => string = cond([
  [
    equals(Calculators.buildingHeadcountSeatcount),
    always('Building Headcount and Seat Count Growth Calculator'),
  ],
  [
    equals(Calculators.buildingHeadcountSeatcountDepartments),
    always('Building Headcount and Seat Count (Dept Level) Growth Calculator'),
  ],
  [
    equals(Calculators.floorHeadcountSeatcount),
    always('Floor Headcount and Seat Count Growth Calculator'),
  ],
  [equals(Calculators.buildingUsf), always('Building USF Growth Calculator')],
  [equals(Calculators.floorUsf), always('Floor USF Growth Calculator')],
  [
    equals(Calculators.macroProgramming),
    always('Macro Programming Calculator'),
  ],
  [equals(Calculators.totalProgram), always('Total Program')],
  [T, always('invalid calculator type save cat')],
]);

export const dupCategoryFromCalculator: (
  calculator: CalculatorType
) => string = cond([
  [
    equals(Calculators.buildingHeadcountSeatcount),
    always('Building Program Scenario'),
  ],
  [
    equals(Calculators.buildingHeadcountSeatcountDepartments),
    always('Building Program Scenario'),
  ],
  [
    equals(Calculators.floorHeadcountSeatcount),
    always('Floor Program Scenario'),
  ],
  [equals(Calculators.buildingUsf), always('Building Program Scenario')],
  [equals(Calculators.floorUsf), always('Floor Program Scenario')],
  [equals(Calculators.macroProgramming), always('Macro Programming')],
  [equals(Calculators.totalProgram), always('Total Program')],
  [T, always('invalid calculator type dupe cat')],
]);
